// extra-fx-codec — codec-specific databending effects
// Datamosh, PNG filter-byte corruption, JPEG DCT manipulation, GIF palette swap, WebP corruption

const N = (v) => Number(v) || 0;

// ---- Datamosh (P-frame bleeding simulation) ----
const datamosh = async (canvas, ctx, bands, shift) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const src = img.data;
    const out = new Uint8ClampedArray(src);
    const numBands = Math.max(1, Math.floor(N(bands)));
    const maxShift = Math.max(1, Math.floor(N(shift)));
    let y = 0;
    for (let b = 0; b < numBands && y < h; b++) {
        const bandH = Math.max(1, Math.floor(2 + Math.random() * (h / numBands)));
        const dy = Math.floor((Math.random() * 2 - 1) * maxShift);
        const endY = Math.min(h, y + bandH);
        for (let yy = y; yy < endY; yy++) {
            for (let x = 0; x < w; x++) {
                const sx = ((x + dy) % w + w) % w;
                const si = (yy * w + sx) * 4, di = (yy * w + x) * 4;
                out[di] = src[si]; out[di+1] = src[si+1]; out[di+2] = src[si+2]; out[di+3] = 255;
            }
        }
        y = endY + Math.floor(Math.random() * 3);
    }
    ctx.putImageData(new ImageData(out, w, h), 0, 0);
};

// ---- PNG filter-byte corruption ----
// PNG scanlines have a filter byte (0-4) before each row.
// Changing filter bytes to wrong types creates authentic PNG databend artifacts.
const pngCorrupt = async (canvas, ctx, amt) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const out = new Uint8ClampedArray(data.length);
    const probability = N(amt) / 100;
    for (let y = 0; y < h; y++) {
        const filterType = Math.random() < probability ? Math.floor(Math.random() * 5) : 0;
        const rowStart = y * w * 4;
        for (let x = 0; x < w; x++) {
            const i = rowStart + x * 4;
            let r = data[i], g = data[i+1], b = data[i+2];
            if (filterType === 1) { // Sub
                if (x > 0) { r = (data[i] + data[i-4]) & 255; g = (data[i+1] + data[i-3]) & 255; b = (data[i+2] + data[i-2]) & 255; }
            } else if (filterType === 2) { // Up
                if (y > 0) { r = (data[i] + data[i - w*4]) & 255; g = (data[i+1] + data[i+1 - w*4]) & 255; b = (data[i+2] + data[i+2 - w*4]) & 255; }
            } else if (filterType === 3) { // Average
                const pl = x > 0 ? data[i-4] : 0, pu = y > 0 ? data[i - w*4] : 0;
                r = (data[i] + Math.floor((pl + pu) / 2)) & 255;
                const plg = x > 0 ? data[i-3] : 0, pug = y > 0 ? data[i+1 - w*4] : 0;
                g = (data[i+1] + Math.floor((plg + pug) / 2)) & 255;
                const plb = x > 0 ? data[i-2] : 0, pub = y > 0 ? data[i+2 - w*4] : 0;
                b = (data[i+2] + Math.floor((plb + pub) / 2)) & 255;
            } else if (filterType === 4) { // Paeth
                const pl = x > 0 ? data[i-4] : 0, pu = y > 0 ? data[i - w*4] : 0, pul = (x > 0 && y > 0) ? data[i-4 - w*4] : 0;
                r = (data[i] + paeth(pl, pu, pul)) & 255;
                const plg = x > 0 ? data[i-3] : 0, pug = y > 0 ? data[i+1 - w*4] : 0, pulg = (x > 0 && y > 0) ? data[i-3 - w*4] : 0;
                g = (data[i+1] + paeth(plg, pug, pulg)) & 255;
                const plb = x > 0 ? data[i-2] : 0, pub = y > 0 ? data[i+2 - w*4] : 0, pulb = (x > 0 && y > 0) ? data[i-2 - w*4] : 0;
                b = (data[i+2] + paeth(plb, pub, pulb)) & 255;
            }
            out[i] = r; out[i+1] = g; out[i+2] = b; out[i+3] = 255;
        }
    }
    ctx.putImageData(new ImageData(out, w, h), 0, 0);
};
function paeth(a, b, c) {
    const p = a + b - c, pa = Math.abs(p - a), pb = Math.abs(p - b), pc = Math.abs(p - c);
    if (pa <= pb && pa <= pc) return a; if (pb <= pc) return b; return c;
}

// ---- JPEG DCT coefficient manipulation ----
const dctCos = new Float64Array(64);
for (let i = 0; i < 8; i++) for (let j = 0; j < 8; j++) dctCos[i*8+j] = Math.cos((2*i+1)*j*Math.PI/16);
function forwardDCT(block) {
    const out = new Float64Array(64);
    for (let u = 0; u < 8; u++) for (let v = 0; v < 8; v++) {
        let sum = 0;
        for (let x = 0; x < 8; x++) for (let y = 0; y < 8; y++) sum += block[x*8+y] * dctCos[x*8+u] * dctCos[y*8+v];
        const cu = u === 0 ? 0.7071 : 1, cv = v === 0 ? 0.7071 : 1;
        out[u*8+v] = 0.25 * cu * cv * sum;
    }
    return out;
}
function inverseDCT(block) {
    const out = new Float64Array(64);
    for (let x = 0; x < 8; x++) for (let y = 0; y < 8; y++) {
        let sum = 0;
        for (let u = 0; u < 8; u++) for (let v = 0; v < 8; v++) {
            const cu = u === 0 ? 0.7071 : 1, cv = v === 0 ? 0.7071 : 1;
            sum += cu * cv * block[u*8+v] * dctCos[x*8+u] * dctCos[y*8+v];
        }
        out[x*8+y] = 0.25 * sum;
    }
    return out;
}
const dctGlitch = async (canvas, ctx, mode, strength) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const m = Math.floor(N(mode)); // 0=zero AC, 1=swap blocks, 2=quantize, 3=shift
    const str = N(strength) / 100;
    for (let by = 0; by < h; by += 8) {
        for (let bx = 0; bx < w; bx += 8) {
            const bw = Math.min(8, w - bx), bh = Math.min(8, h - by);
            // Extract Y channel (luminance) for this block
            const yBlock = new Float64Array(64);
            for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) {
                const i = ((by+y) * w + (bx+x)) * 4;
                yBlock[x*8+y] = 0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2] - 128;
            }
            const dct = forwardDCT(yBlock);
            if (m === 0) { // Zero out AC coefficients above threshold
                const keep = Math.max(1, Math.floor((1 - str) * 8));
                for (let u = 0; u < 8; u++) for (let v = 0; v < 8; v++) if (u + v > keep) dct[u*8+v] = 0;
            } else if (m === 1) { // Swap random coefficient blocks
                if (Math.random() < str) {
                    const u1 = Math.floor(Math.random()*8), v1 = Math.floor(Math.random()*8);
                    const u2 = Math.floor(Math.random()*8), v2 = Math.floor(Math.random()*8);
                    const tmp = dct[u1*8+v1]; dct[u1*8+v1] = dct[u2*8+v2]; dct[u2*8+v2] = tmp;
                }
            } else if (m === 2) { // Quantize
                const q = 1 + Math.floor(str * 50);
                for (let i = 0; i < 64; i++) dct[i] = Math.round(dct[i] / q) * q;
            } else { // Shift coefficients
                const sh = Math.floor(str * 4);
                for (let u = 7; u >= sh; u--) for (let v = 0; v < 8; v++) dct[u*8+v] = dct[(u-sh)*8+v];
                for (let u = 0; u < sh; u++) for (let v = 0; v < 8; v++) dct[u*8+v] = 0;
            }
            const idct = inverseDCT(dct);
            for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) {
                const i = ((by+y) * w + (bx+x)) * 4;
                const yVal = Math.max(0, Math.min(255, idct[x*8+y] + 128));
                // Apply Y change to all channels (preserves color ratio)
                const oldY = 0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2];
                const ratio = oldY > 0 ? yVal / oldY : 1;
                data[i] = Math.min(255, data[i] * ratio);
                data[i+1] = Math.min(255, data[i+1] * ratio);
                data[i+2] = Math.min(255, data[i+2] * ratio);
            }
        }
    }
    ctx.putImageData(img, 0, 0);
};

// ---- GIF palette swap ----
const gifPalette = async (canvas, ctx, amt) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const probability = N(amt) / 100;
    // Build a 256-color palette via uniform quantization
    const pal = new Uint8Array(256 * 3);
    for (let r = 0; r < 8; r++) for (let g = 0; g < 8; g++) for (let b = 0; b < 4; b++) {
        const idx = (r * 32 + g * 4 + b);
        pal[idx*3] = r * 36; pal[idx*3+1] = g * 36; pal[idx*3+2] = b * 64;
    }
    // Build swap map
    const swap = new Uint8Array(256);
    for (let i = 0; i < 256; i++) swap[i] = i;
    for (let i = 0; i < 256; i++) if (Math.random() < probability) swap[i] = Math.floor(Math.random() * 256);
    // Apply
    for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i+1], b = data[i+2];
        const idx = (r >> 5) * 32 + (g >> 5) * 4 + (b >> 6);
        const sw = swap[idx];
        data[i] = pal[sw*3]; data[i+1] = pal[sw*3+1]; data[i+2] = pal[sw*3+2];
    }
    ctx.putImageData(img, 0, 0);
};

// ---- WebP corruption ----
const webpCorrupt = async (canvas, ctx, amt) => {
    const w = canvas.width, h = canvas.height;
    const probability = N(amt) / 100;
    try {
        const blob = await new Promise(res => canvas.toBlob(res, 'image/webp', 0.8));
        if (!blob) throw new Error('no webp');
        const buf = new Uint8Array(await blob.arrayBuffer());
        // Corrupt bytes in the data region (skip first 20 bytes header)
        for (let i = 20; i < buf.length; i++) if (Math.random() < probability) buf[i] = Math.floor(Math.random() * 256);
        const corruptBlob = new Blob([buf], { type: 'image/webp' });
        const bmp = await createImageBitmap(corruptBlob);
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(bmp, 0, 0, w, h);
        bmp.close();
    } catch (e) {
        // Fallback: if WebP not supported or decode fails, do byte-level noise
        const img = ctx.getImageData(0, 0, w, h);
        const data = img.data;
        for (let i = 0; i < data.length; i += 4) if (Math.random() < probability) {
            data[i] = Math.random() * 256; data[i+1] = Math.random() * 256; data[i+2] = Math.random() * 256;
        }
        ctx.putImageData(img, 0, 0);
    }
};

const descriptors = [
    { name: 'dm-datamosh', inputs: [
        { name: 'Bands', type: 'knob', min: 1, max: 50, step: 1, value: 10 },
        { name: 'Shift', type: 'knob', min: 1, max: 200, step: 1, value: 50 }], fn: datamosh },
    { name: 'png-corrupt', inputs: [
        { name: 'Amount', type: 'knob', min: 0, max: 100, step: 1, value: 30 }], fn: pngCorrupt },
    { name: 'dct-glitch', inputs: [
        { name: 'Mode', type: 'knob', min: 0, max: 3, step: 1, value: 0 },
        { name: 'Strength', type: 'knob', min: 0, max: 100, step: 1, value: 50 }], fn: dctGlitch },
    { name: 'gif-palette', inputs: [
        { name: 'Amount', type: 'knob', min: 0, max: 100, step: 1, value: 30 }], fn: gifPalette },
    { name: 'webp-corrupt', inputs: [
        { name: 'Amount', type: 'knob', min: 0, max: 100, step: 1, value: 20 }], fn: webpCorrupt },
];

const lib = {};
descriptors.forEach(d => lib[d.name] = d.fn);

module.exports = { descriptors, lib };
